import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/*
 * ================================================================
 * ASTRAL — useRealTimeEngine
 * ================================================================
 *
 * Motor central do frontend para:
 *
 * 1. receber eventos em tempo real por SSE;
 * 2. fazer polling silencioso como fallback;
 * 3. atualizar páginas sem reload;
 * 4. exibir toast;
 * 5. emitir som;
 * 6. falar a notificação por voz;
 * 7. mostrar notificação nativa do navegador/Windows;
 * 8. evitar duplicidade entre SSE e polling;
 * 9. reconectar automaticamente;
 * 10. respeitar notifications_config;
 * 11. trabalhar com a autenticação Web baseada em cookie;
 * 12. sincronizar silenciosamente ao voltar para a aba/rede.
 *
 * IMPORTANTE:
 *
 * O backend Web usa cookies HttpOnly para a sessão.
 * EventSource não permite adicionar Authorization: Bearer
 * manualmente. Portanto o SSE usa withCredentials: true.
 *
 * O backend atual publica:
 *
 *     event: connected
 *     event: notification
 *
 * O evento "notification" precisa ser registrado explicitamente
 * com addEventListener(). onmessage sozinho NÃO é suficiente.
 *
 * O polling NÃO dispara notificações para o histórico existente
 * na primeira sincronização. Ele só apresenta itens novos depois
 * do baseline inicial.
 * ================================================================
 */

const DEFAULT_CONFIG = {
  enabled: true,
  realtime: true,
  polling: true,

  // Fallback silencioso: 5 segundos.
  polling_interval_ms: 5000,

  toast: true,
  sound: true,
  voice: true,
  browser: true,

  sound_volume: 0.55,
  toast_duration: 7000,

  priorities: {
    critical: { enabled: true, sound: true, voice: true, browser: true, repeat: 1, toast_duration: 9000, sound_frequency: 1040 },
    high: { enabled: true, sound: true, voice: true, browser: true, repeat: 1, toast_duration: 8000, sound_frequency: 880 },
    medium: { enabled: true, sound: true, voice: false, browser: true, repeat: 1, toast_duration: 7000, sound_frequency: 740 },
    low: { enabled: true, sound: false, voice: false, browser: false, repeat: 1, toast_duration: 5000, sound_frequency: 620 },
  },

  voice_config: {
    lang: "pt-BR",
    rate: 1,
    pitch: 1,
    volume: 1,
    voice_name: "",
  },

  events: {},
};

const REALTIME_EVENT = "astral:realtime";
const NOTIFICATION_EVENT = "astral:notification";
const REFRESH_EVENT = "astral:refresh";

/*
 * PRESENÇA GEOESPACIAL (FASE 5)
 *
 * O backend publica o evento SSE nomeado `geo.presence` (routers/
 * geo_presence_routes.py) SEMPRE com escopo já aplicado. Ele NÃO é uma
 * notificação: não deve gerar toast, som ou voz. Por isso é redistribuído
 * num evento próprio do navegador, consumido pela Central Geoespacial.
 */
const GEO_PRESENCE_EVENT = "astral:geo-presence";


// ================================================================
// CONFIGURAÇÃO
// ================================================================

function mergeConfig(value) {
  const source =
    value &&
    typeof value === "object"
      ? value
      : {};

  return {
    ...DEFAULT_CONFIG,
    ...source,

    voice_config: {
      ...DEFAULT_CONFIG.voice_config,
      ...(source.voice_config || {}),
    },

    events: {
      ...DEFAULT_CONFIG.events,
      ...(source.events || {}),
    },

    priorities: {
      ...DEFAULT_CONFIG.priorities,
      ...(source.priorities || {}),
      critical: { ...DEFAULT_CONFIG.priorities.critical, ...(source.priorities?.critical || {}) },
      high: { ...DEFAULT_CONFIG.priorities.high, ...(source.priorities?.high || {}) },
      medium: { ...DEFAULT_CONFIG.priorities.medium, ...(source.priorities?.medium || {}) },
      low: { ...DEFAULT_CONFIG.priorities.low, ...(source.priorities?.low || {}) },
    },
  };
}


// ================================================================
// JSON SEGURO
// ================================================================

function safeJson(value) {
  if (value == null) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return {
      message: String(value),
    };
  }
}


// ================================================================
// EVENTO HABILITADO?
// ================================================================

function eventEnabled(
  config,
  eventType,
) {
  if (!eventType) {
    return true;
  }

  const events =
    config?.events || {};

  const rule =
    events[eventType];

  if (rule == null) {
    return true;
  }

  if (
    typeof rule ===
    "boolean"
  ) {
    return rule;
  }

  if (
    typeof rule ===
    "object"
  ) {
    return rule.enabled !== false;
  }

  return true;
}


// ================================================================
// NORMALIZAÇÃO DO EVENTO
// ================================================================

function normalizeEvent(raw) {
  const source =
    safeJson(raw) || {};

  /*
   * O SSE atual envia:
   *
   * {
   *   "notification": {
   *      "id": "...",
   *      "title": "...",
   *      "body": "...",
   *      "speech_text": "..."
   *   }
   * }
   *
   * O polling retorna a notificação diretamente.
   */

  const data =
    source.data ||
    source.payload ||
    source.notification ||
    source;

  const notification =
    source.notification ||
    (
      source.id &&
      (
        source.title ||
        source.body ||
        source.message
      )
        ? source
        : null
    );

  const id =
    source.id ||
    source.notification_id ||
    data?.id ||
    data?.notification_id;

  return {
    ...source,

    data,

    id:
      id ||
      `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`,

    type:
      source.type ||
      source.event ||
      source.event_type ||
      data?.type ||
      data?.event ||
      (
        notification
          ? "notification"
          : "realtime"
      ),

    title:
      source.title ||
      notification?.title ||
      data?.title ||
      "ASTRAL",

    message:
      source.message ||
      source.body ||
      notification?.body ||
      notification?.message ||
      data?.message ||
      data?.body ||
      "Nova atualização disponível.",

    notification,

    timestamp:
      source.timestamp ||
      source.created_at ||
      notification?.created_at ||
      data?.timestamp ||
      new Date().toISOString(),
  };
}


// ================================================================
// URL DO SSE
// ================================================================
//
// Não colocamos token na URL.
//
// A autenticação Web oficial utiliza cookie HttpOnly.
// EventSource usa withCredentials: true.
//
// Se api.defaults.baseURL for:
//   /api
//   https://servidor/api
//   /api/
// todos os formatos são normalizados.
// ================================================================

function buildSseUrl(
  customUrl,
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return customUrl ||
      "/api/notifications/stream";
  }

  if (customUrl) {
    try {
      return new URL(
        customUrl,
        window.location.origin,
      ).toString();
    } catch {
      return customUrl;
    }
  }

  const base =
    api?.defaults?.baseURL;

  if (!base) {
    return new URL(
      "/api/notifications/stream",
      window.location.origin,
    ).toString();
  }

  try {
    const baseUrl =
      new URL(
        base,
        window.location.origin,
      );

    /*
     * Evita:
     *
     * /api//notifications/stream
     */
    const pathname =
      baseUrl.pathname.replace(
        /\/+$/,
        "",
      );

    baseUrl.pathname =
      `${pathname}/notifications/stream`
        .replace(
          /\/{2,}/g,
          "/",
        );

    return baseUrl.toString();
  } catch {
    const normalized =
      String(base)
        .replace(/\/+$/, "");

    return `${normalized}/notifications/stream`;
  }
}


// ================================================================
// PRIORIDADE DA NOTIFICAÇÃO
// ================================================================

function getNotificationPriority(notification) {
  const raw = String(
    notification?.priority ||
    notification?.severity ||
    notification?.criticality ||
    notification?.occurrence_priority ||
    "",
  ).trim().toLowerCase();

  if (["critical", "critica", "crítica", "critico", "crítico"].includes(raw)) return "critical";
  if (["high", "alta"].includes(raw)) return "high";
  if (["medium", "media", "média"].includes(raw)) return "medium";
  return "low";
}

function getPriorityConfig(config, notification) {
  const priority = getNotificationPriority(notification);
  return {
    priority,
    ...(config?.priorities?.[priority] || {}),
  };
}

// ================================================================
// SOM
// ================================================================

function playAlertSound(
  volume = 0.55,
  frequency = 880,
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  const AudioContextClass =
    window.AudioContext ||
    window.webkitAudioContext;

  if (!AudioContextClass) {
    return;
  }

  try {
    const context =
      new AudioContextClass();

    const oscillator =
      context.createOscillator();

    const gain =
      context.createGain();

    const safeVolume =
      Math.min(
        1,
        Math.max(
          0,
          Number(
            volume ?? 0.55,
          ),
        ),
      );

    oscillator.type =
      "sine";

    const safeFrequency = Math.max(
      220,
      Math.min(1800, Number(frequency) || 880),
    );

    oscillator.frequency.setValueAtTime(
      safeFrequency,
      context.currentTime,
    );

    oscillator.frequency.exponentialRampToValueAtTime(
      safeFrequency * 0.75,
      context.currentTime + 0.12,
    );

    gain.gain.setValueAtTime(
      0,
      context.currentTime,
    );

    gain.gain.linearRampToValueAtTime(
      safeVolume,
      context.currentTime +
        0.01,
    );

    gain.gain.exponentialRampToValueAtTime(
      0.001,
      context.currentTime +
        0.2,
    );

    oscillator.connect(
      gain,
    );

    gain.connect(
      context.destination,
    );

    oscillator.start();

    oscillator.stop(
      context.currentTime +
        0.2,
    );

    window.setTimeout(
      () => {
        context
          .close()
          .catch(() => {});
      },
      400,
    );
  } catch {
    // Som é opcional.
  }
}


// ================================================================
// VOZ
// ================================================================

function speakAlert(
  text,
  voiceConfig = {},
) {
  if (
    typeof window ===
      "undefined" ||
    !(
      "speechSynthesis" in
      window
    ) ||
    !text
  ) {
    return;
  }

  try {
    window.speechSynthesis.cancel();

    const utterance =
      new SpeechSynthesisUtterance(
        String(text),
      );

    utterance.lang =
      voiceConfig.lang ||
      "pt-BR";

    utterance.rate =
      Number(
        voiceConfig.rate ??
          1,
      );

    utterance.pitch =
      Number(
        voiceConfig.pitch ??
          1,
      );

    utterance.volume =
      Math.min(
        1,
        Math.max(
          0,
          Number(
            voiceConfig.volume ??
              1,
          ),
        ),
      );

    const voices =
      window.speechSynthesis.getVoices?.() || [];

    if (voiceConfig.voice_name && voices.length) {
      const selectedVoice = voices.find(
        (voice) => voice.name === voiceConfig.voice_name,
      );

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
    }

    window.speechSynthesis.speak(
      utterance,
    );
  } catch {
    // Voz é opcional.
  }
}


// ================================================================
// NOTIFICAÇÃO NATIVA
// ================================================================

function showBrowserNotification(
  title,
  body,
  data,
) {
  if (
    typeof window ===
      "undefined" ||
    !(
      "Notification" in
      window
    )
  ) {
    return false;
  }

  if (
    Notification.permission !==
    "granted"
  ) {
    return false;
  }

  try {
    const notification =
      new Notification(
        title || "ASTRAL",
        {
          body:
            body ||
            "Nova atualização disponível.",

          tag:
            data?.id ||
            `astral-${Date.now()}`,

          silent: true,
        },
      );

    notification.onclick =
      () => {
        try {
          window.focus();
        } catch {
          // noop
        }

        window.dispatchEvent(
          new CustomEvent(
            NOTIFICATION_EVENT,
            {
              detail: {
                action:
                  "click",
                data,
              },
            },
          ),
        );

        notification.close();
      };

    return true;
  } catch {
    return false;
  }
}


// ================================================================
// EVENTOS GLOBAIS
// ================================================================

function dispatchRefresh(
  detail,
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      REFRESH_EVENT,
      {
        detail,
      },
    ),
  );

  window.dispatchEvent(
    new CustomEvent(
      REALTIME_EVENT,
      {
        detail,
      },
    ),
  );
}


// ================================================================
// POLLING
// ================================================================

function extractPollingItems(
  response,
) {
  const data =
    response?.data;

  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.notifications,
    )
  ) {
    return data.notifications;
  }

  if (
    Array.isArray(
      data?.items,
    )
  ) {
    return data.items;
  }

  if (
    Array.isArray(
      data?.events,
    )
  ) {
    return data.events;
  }

  return [];
}


// ================================================================
// HOOK
// ================================================================

export function useRealTimeEngine(
  options = {},
) {
  const {
    enabled = true,

    /*
     * Pode ser sobrescrito pelo chamador.
     * Por padrão usamos a baseURL oficial da API.
     */
    sseUrl:
      customSseUrl,

    pollingUrl =
      "/notifications",

    config:
      externalConfig,

    onEvent,
    onNotification,
    onRefresh,

    pollingInterval,
  } = options;

  const [connected, setConnected] =
    useState(false);

  const [lastEvent, setLastEvent] =
    useState(null);

  const [lastError, setLastError] =
    useState(null);

  const [
    notificationPermission,
    setNotificationPermission,
  ] = useState(
    typeof window !==
      "undefined" &&
    "Notification" in
      window
      ? Notification.permission
      : "unsupported",
  );

  const eventSourceRef =
    useRef(null);

  const reconnectTimerRef =
    useRef(null);

  const pollingTimerRef =
    useRef(null);

  const mountedRef =
    useRef(false);

  /*
   * Eventos/notificações já processados.
   *
   * Impede duplicidade entre:
   *
   * SSE → evento novo
   * polling → mesmo evento
   */
  const seenEventsRef =
    useRef(new Map());

  /*
   * Primeiro polling é apenas baseline.
   *
   * Não deve falar/emitir toast das notificações antigas
   * que já estavam no banco antes da página abrir.
   */
  const pollingInitializedRef =
    useRef(false);

  const configRef =
    useRef(
      mergeConfig(
        externalConfig,
      ),
    );

  const sseUrlRef =
    useRef(
      buildSseUrl(
        customSseUrl,
      ),
    );


  // ==============================================================
  // CONFIG ATUAL
  // ==============================================================

  useEffect(() => {
    configRef.current =
      mergeConfig(
        externalConfig,
      );
  }, [
    externalConfig,
  ]);

  useEffect(() => {
    sseUrlRef.current =
      buildSseUrl(
        customSseUrl,
      );
  }, [
    customSseUrl,
  ]);


  // ==============================================================
  // MARCA EVENTO COMO VISTO
  // ==============================================================

  const markSeen =
    useCallback(
      (id) => {
        if (!id) {
          return false;
        }

        const now =
          Date.now();

        const map =
          seenEventsRef.current;

        /*
         * Mantém a memória limitada.
         */
        for (
          const [
            key,
            timestamp,
          ] of map.entries()
        ) {
          if (
            now - timestamp >
            10 * 60 * 1000
          ) {
            map.delete(key);
          }
        }

        if (
          map.has(id)
        ) {
          return true;
        }

        map.set(
          id,
          now,
        );

        return false;
      },
      [],
    );


  // ==============================================================
  // PROCESSAMENTO
  // ==============================================================

  const processEvent =
    useCallback(
      (
        rawEvent,
        source = "sse",
        optionsForEvent = {},
      ) => {
        if (
          !mountedRef.current
        ) {
          return;
        }

        const config =
          configRef.current;

        if (
          !enabled ||
          config.enabled ===
            false
        ) {
          return;
        }

        const event =
          normalizeEvent(
            rawEvent,
          );

        if (
          markSeen(
            event.id,
          )
        ) {
          return;
        }

        if (
          !eventEnabled(
            config,
            event.type,
          )
        ) {
          return;
        }

        setLastEvent(
          event,
        );

        setLastError(
          null,
        );

        const detail = {
          ...event,
          source,
          received_at:
            new Date().toISOString(),
        };

        /*
         * Atualização global das páginas.
         *
         * Mesmo que a apresentação de notificação esteja
         * desligada, o evento pode continuar sendo usado
         * para sincronização se o motor estiver habilitado.
         */
        if (
          optionsForEvent.silentRefresh !==
          false
        ) {
          dispatchRefresh(
            detail,
          );
        }

        onEvent?.(
          detail,
        );

        onRefresh?.(
          detail,
        );

        /*
         * Eventos de sincronização podem ser silenciosos.
         */
        if (
          optionsForEvent.silentNotify
        ) {
          return;
        }

        const notification =
          event.notification ||
          event.data
            ?.notification ||
          (
            event.id &&
            (
              event.title ||
              event.message
            )
              ? event
              : null
          );

        if (!notification) {
          return;
        }

        const title =
          notification.title ||
          event.title ||
          "ASTRAL";

        /*
         * Backend atual usa "body".
         * Algumas versões podem usar "message".
         */
        const message =
          notification.body ||
          notification.message ||
          event.message ||
          "Nova atualização disponível.";

        const shouldNotify =
          notification.notify !==
          false;

        if (!shouldNotify) {
          return;
        }

        const priorityConfig =
          getPriorityConfig(config, notification);

        if (priorityConfig.enabled === false) {
          return;
        }

        if (config.toast !== false) {
          toast(title, {
            description: message,
            duration: Number(
              priorityConfig.toast_duration ??
                config.toast_duration ??
                7000,
            ),
            id: notification.id,
          });
        }

        if (
          config.sound !== false &&
          priorityConfig.sound !== false
        ) {
          const repeat = Math.max(
            1,
            Math.min(5, Number(priorityConfig.repeat) || 1),
          );

          for (let index = 0; index < repeat; index += 1) {
            window.setTimeout(
              () =>
                playAlertSound(
                  config.sound_volume,
                  priorityConfig.sound_frequency,
                ),
              index * 450,
            );
          }
        }

        if (
          config.voice !== false &&
          priorityConfig.voice !== false
        ) {
          const voiceText =
            notification.speech_text ||
            notification.voice_text ||
            message ||
            title;

          speakAlert(
            voiceText,
            config.voice_config,
          );
        }

        if (
          config.browser !== false &&
          priorityConfig.browser !== false
        ) {
          showBrowserNotification(
            title,
            message,
            {
              ...detail,
              priority: priorityConfig.priority,
            },
          );
        }

        onNotification?.({
          ...detail,
          priority: priorityConfig.priority,
          priority_config: priorityConfig,
        });

        if (
          typeof window !==
          "undefined"
        ) {
          window.dispatchEvent(
            new CustomEvent(
              NOTIFICATION_EVENT,
              {
                detail,
              },
            ),
          );
        }
      },
      [
        enabled,
        markSeen,
        onEvent,
        onNotification,
        onRefresh,
      ],
    );


  // ==============================================================
  // PARAR SSE
  // ==============================================================

  const stopSSE =
    useCallback(
      () => {
        if (
          eventSourceRef.current
        ) {
          try {
            eventSourceRef.current.close();
          } catch {
            // noop
          }

          eventSourceRef.current =
            null;
        }

        setConnected(
          false,
        );
      },
      [],
    );


  // ==============================================================
  // INICIAR SSE
  // ==============================================================

  const startSSE =
    useCallback(
      () => {
        if (
          typeof window ===
            "undefined" ||
          typeof EventSource ===
            "undefined"
        ) {
          return false;
        }

        const config =
          configRef.current;

        if (
          !enabled ||
          config.enabled ===
            false ||
          config.realtime ===
            false
        ) {
          setConnected(
            false,
          );

          return false;
        }

        /*
         * Se já existe uma conexão aberta/connecting,
         * não criamos uma segunda.
         */
        const existing =
          eventSourceRef.current;

        if (
          existing &&
          (
            existing.readyState ===
              EventSource.OPEN ||
            existing.readyState ===
              EventSource.CONNECTING
          )
        ) {
          return true;
        }

        /*
         * Cancela tentativa anterior.
         */
        if (
          reconnectTimerRef.current
        ) {
          window.clearTimeout(
            reconnectTimerRef.current,
          );

          reconnectTimerRef.current =
            null;
        }

        const url =
          sseUrlRef.current ||
          buildSseUrl();

        try {
          /*
           * A sessão Web é autenticada pelo cookie HttpOnly.
           *
           * NÃO colocamos JWT na query string.
           */
          const source =
            new EventSource(
              url,
              {
                withCredentials:
                  true,
              },
            );

          eventSourceRef.current =
            source;

          source.onopen =
            () => {
              if (
                !mountedRef.current
              ) {
                return;
              }

              setConnected(
                true,
              );

              setLastError(
                null,
              );
            };

          /*
           * ======================================================
           * EVENTO NOMEADO DO BACKEND
           * ======================================================
           *
           * O backend envia:
           *
           * event: notification
           * data: {"notification": {...}}
           *
           * Portanto precisamos deste listener.
           */
          source.addEventListener(
            "notification",
            (message) => {
              processEvent(
                message.data,
                "sse",
              );
            },
          );

          /*
           * PRESENÇA GEOESPACIAL (FASE 5).
           *
           * event: geo.presence
           * data: { action, presence: {...} }
           *
           * NÃO passa por processEvent: presença não é notificação e não deve
           * gerar toast, som, voz nem histórico. Apenas redistribui.
           */
          source.addEventListener(
            "geo.presence",
            (message) => {
              if (!mountedRef.current) {
                return;
              }

              let payload = null;

              try {
                payload = JSON.parse(message.data);
              } catch (error) {
                payload = null;
              }

              if (!payload) {
                return;
              }

              window.dispatchEvent(
                new CustomEvent(
                  GEO_PRESENCE_EVENT,
                  {
                    detail: payload,
                  },
                ),
              );
            },
          );

          /*
           * Evento de conexão.
           *
           * Não gera popup, som ou voz.
           */
          source.addEventListener(
            "connected",
            () => {
              if (
                !mountedRef.current
              ) {
                return;
              }

              setConnected(
                true,
              );

              setLastError(
                null,
              );
            },
          );

          /*
           * Compatibilidade com SSE sem "event:".
           *
           * IMPORTANTE:
           *
           * O backend atual usa event: notification, portanto
           * esse handler NÃO duplica o evento atual.
           */
          source.onmessage =
            (message) => {
              processEvent(
                message.data,
                "sse",
              );
            };

          source.onerror =
            (error) => {
              if (
                !mountedRef.current
              ) {
                return;
              }

              setConnected(
                false,
              );

              setLastError(
                error,
              );

              /*
               * Fecha a conexão com erro e cria uma nova
               * tentativa controlada.
               */
              try {
                source.close();
              } catch {
                // noop
              }

              if (
                eventSourceRef.current ===
                source
              ) {
                eventSourceRef.current =
                  null;
              }

              if (
                reconnectTimerRef.current
              ) {
                window.clearTimeout(
                  reconnectTimerRef.current,
                );
              }

              reconnectTimerRef.current =
                window.setTimeout(
                  () => {
                    reconnectTimerRef.current =
                      null;

                    if (
                      mountedRef.current
                    ) {
                      startSSE();
                    }
                  },
                  3000,
                );
            };

          return true;
        } catch (error) {
          setConnected(
            false,
          );

          setLastError(
            error,
          );

          return false;
        }
      },
      [
        enabled,
        processEvent,
      ],
    );


  // ==============================================================
  // POLLING SILENCIOSO
  // ==============================================================

  const runPolling =
    useCallback(
      async (
        optionsForPolling = {},
      ) => {
        const config =
          configRef.current;

        if (
          !enabled ||
          config.enabled ===
            false ||
          config.polling ===
            false
        ) {
          return;
        }

        const silent =
          optionsForPolling.silent ===
          true;

        try {
          const response =
            await api.get(
              pollingUrl,
              {
                params: {
                  realtime:
                    true,

                  unread_only:
                    true,
                },
              },
            );

          const items =
            extractPollingItems(
              response,
            );

          /*
           * PRIMEIRO POLLING:
           *
           * É somente baseline.
           *
           * Não queremos que ao abrir o portal o sistema:
           *
           * - fale 20 notificações antigas;
           * - toque 20 sons;
           * - gere 20 toasts.
           */
          if (
            !pollingInitializedRef.current
          ) {
            for (
              const item of items
            ) {
              const event =
                normalizeEvent(
                  item,
                );

              markSeen(
                event.id,
              );
            }

            pollingInitializedRef.current =
              true;

            setLastError(
              null,
            );

            return;
          }

          /*
           * POLLINGS POSTERIORES:
           *
           * Só itens que ainda não foram vistos entram no
           * processamento normal.
           */
          for (
            const item of items
          ) {
            processEvent(
              item,
              "polling",
              {
                silentNotify:
                  silent,
              },
            );
          }

          setLastError(
            null,
          );
        } catch (error) {
          /*
           * Falha de polling nunca derruba o portal.
           * O SSE continua sendo o canal principal.
           */
          setLastError(
            error,
          );
        }
      },
      [
        enabled,
        markSeen,
        pollingUrl,
        processEvent,
      ],
    );


  // ==============================================================
  // INICIAR POLLING
  // ==============================================================

  const startPolling =
    useCallback(
      () => {
        if (
          typeof window ===
          "undefined"
        ) {
          return;
        }

        const config =
          configRef.current;

        if (
          !enabled ||
          config.enabled ===
            false ||
          config.polling ===
            false
        ) {
          return;
        }

        if (
          pollingTimerRef.current
        ) {
          window.clearInterval(
            pollingTimerRef.current,
          );
        }

        const interval =
          Math.max(
            3000,
            Number(
              pollingInterval ??
                config.polling_interval_ms ??
                5000,
            ),
          );

        /*
         * Primeiro ciclo:
         * baseline silencioso.
         */
        runPolling({
          silent: true,
        });

        pollingTimerRef.current =
          window.setInterval(
            () => {
              runPolling();
            },
            interval,
          );
      },
      [
        enabled,
        pollingInterval,
        runPolling,
      ],
    );


  // ==============================================================
  // PERMISSÃO DO NAVEGADOR
  // ==============================================================

  const requestBrowserPermission =
    useCallback(
      async () => {
        if (
          typeof window ===
            "undefined" ||
          !(
            "Notification" in
            window
          )
        ) {
          setNotificationPermission(
            "unsupported",
          );

          return "unsupported";
        }

        try {
          const result =
            await Notification.requestPermission();

          setNotificationPermission(
            result,
          );

          return result;
        } catch {
          const current =
            Notification.permission;

          setNotificationPermission(
            current,
          );

          return current;
        }
      },
      [],
    );


  // ==============================================================
  // TESTE
  // ==============================================================

  const testNotification =
    useCallback(
      async () => {
        if (
          typeof window ===
          "undefined"
        ) {
          return;
        }

        /*
         * Se ainda não estiver autorizado, solicita a permissão.
         *
         * A chamada pode ser feita por botão no Layout, portanto
         * ocorre dentro de uma interação do usuário.
         */
        if (
          "Notification" in
            window &&
          Notification.permission !==
            "granted"
        ) {
          await requestBrowserPermission();
        }

        const testEvent = {
          id:
            `test-${Date.now()}`,

          type:
            "notification_test",

          title:
            "ASTRAL — teste de notificação",

          body:
            "As notificações do ASTRAL estão funcionando corretamente.",

          speech_text:
            "ASTRAL. Teste de notificação realizado com sucesso.",

          notification: {
            id:
              `test-${Date.now()}`,

            title:
              "ASTRAL — teste de notificação",

            body:
              "As notificações do ASTRAL estão funcionando corretamente.",

            speech_text:
              "ASTRAL. Teste de notificação realizado com sucesso.",

            notify:
              true,
          },
        };

        /*
         * O teste sempre deve passar mesmo que o administrador
         * tenha desabilitado algum evento específico.
         */
        const previous =
          configRef.current;

        configRef.current = {
          ...previous,

          events: {
            ...previous.events,

            notification_test:
              true,
          },
        };

        processEvent(
          testEvent,
          "test",
        );

        configRef.current =
          previous;
      },
      [
        processEvent,
        requestBrowserPermission,
      ],
    );


  // ==============================================================
  // CICLO DE VIDA
  // ==============================================================

  useEffect(
    () => {
      mountedRef.current =
        true;

      /*
       * Não iniciar nada antes do componente estar montado.
       */
      startSSE();

      startPolling();

      return () => {
        mountedRef.current =
          false;

        stopSSE();

        if (
          reconnectTimerRef.current
        ) {
          window.clearTimeout(
            reconnectTimerRef.current,
          );

          reconnectTimerRef.current =
            null;
        }

        if (
          pollingTimerRef.current
        ) {
          window.clearInterval(
            pollingTimerRef.current,
          );

          pollingTimerRef.current =
            null;
        }

        if (
          typeof window !==
            "undefined" &&
          "speechSynthesis" in
            window
        ) {
          try {
            window.speechSynthesis.cancel();
          } catch {
            // noop
          }
        }
      };
    },
    [
      startPolling,
      startSSE,
      stopSSE,
    ],
  );


  // ==============================================================
  // ABA VOLTOU A FICAR VISÍVEL
  // ==============================================================

  useEffect(
    () => {
      const handleVisibility =
        () => {
          if (
            document.visibilityState !==
            "visible"
          ) {
            return;
          }

          /*
           * Sincronização silenciosa imediata.
           */
          runPolling();

          /*
           * Se o navegador encerrou o SSE enquanto a aba
           * estava em background, abre novamente.
           */
          if (
            !eventSourceRef.current
          ) {
            startSSE();
          }
        };

      document.addEventListener(
        "visibilitychange",
        handleVisibility,
      );

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibility,
        );
      };
    },
    [
      runPolling,
      startSSE,
    ],
  );


  // ==============================================================
  // INTERNET VOLTOU
  // ==============================================================

  useEffect(
    () => {
      const handleOnline =
        () => {
          /*
           * Resetamos apenas a conexão.
           *
           * O mapa de vistos permanece para evitar duplicidade.
           */
          startSSE();

          runPolling();
        };

      window.addEventListener(
        "online",
        handleOnline,
      );

      return () => {
        window.removeEventListener(
          "online",
          handleOnline,
        );
      };
    },
    [
      runPolling,
      startSSE,
    ],
  );


  // ==============================================================
  // RETORNO PÚBLICO
  // ==============================================================

  return {
    connected,
    lastEvent,
    lastError,
    notificationPermission,

    startSSE,
    stopSSE,

    runPolling,
    startPolling,

    requestBrowserPermission,
    testNotification,

    realtimeEventName:
      REALTIME_EVENT,

    notificationEventName:
      NOTIFICATION_EVENT,

    refreshEventName:
      REFRESH_EVENT,
  };
}


// ================================================================
// VOZES DISPONÍVEIS
// ================================================================

export function getAvailableVoices() {
  if (
    typeof window === "undefined" ||
    !("speechSynthesis" in window)
  ) {
    return [];
  }

  return window.speechSynthesis.getVoices?.() || [];
}


// ================================================================
// HELPERS GLOBAIS
// ================================================================


export function emitRealtimeRefresh(
  detail = {},
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      REFRESH_EVENT,
      {
        detail: {
          ...detail,
          local: true,
          timestamp:
            new Date().toISOString(),
        },
      },
    ),
  );
}


export function emitRealtimeEvent(
  detail = {},
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      REALTIME_EVENT,
      {
        detail: {
          ...detail,
          local: true,
          timestamp:
            new Date().toISOString(),
        },
      },
    ),
  );
}


export function emitRealtimeNotification(
  detail = {},
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      NOTIFICATION_EVENT,
      {
        detail: {
          ...detail,
          local: true,
          timestamp:
            new Date().toISOString(),
        },
      },
    ),
  );
}


export const REALTIME_EVENTS = {
  REFRESH:
    REFRESH_EVENT,

  REALTIME:
    REALTIME_EVENT,

  NOTIFICATION:
    NOTIFICATION_EVENT,

  /* Presença geoespacial (FASE 5) — consumida pela Central Geoespacial. */
  GEO_PRESENCE:
    GEO_PRESENCE_EVENT,
};


export default useRealTimeEngine;